import { AdminUser } from 'tools/bff/generated/bff/admin/admin_user_pb';
import { Box, Button, PageHeader, Text } from '@tocktix/pantry';
import { Divider } from 'ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useLocation, useNavigate } from 'react-router-v7';
import { userClient } from 'tools/bff/client';
import UserCards from './UserCards';
import UserSearch from './UserSearch';

const PAGE_SIZE = 25;

interface SavedSearchState {
  query: string;
  scrollY: number;
}

const UsersRoute: React.FunctionComponent = () => {
  const location = useLocation();
  const savedState =
    (location.state as { searchState?: SavedSearchState } | null)?.searchState ?? null;

  const [page, setPage] = useState<number>(0);
  const [query, setQuery] = useState<string>(savedState?.query ?? '');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Sequence counter to discard stale in-flight responses when a newer search fires.
  const searchSeqRef = useRef(0);
  // Stores a deferred scroll offset that should be applied once results are rendered.
  const pendingScrollYRef = useRef<number | null>(null);

  const navigate = useNavigate();

  const handleSearch = useCallback(async (q: string, pageNum: number, append: boolean) => {
    const mySeq = ++searchSeqRef.current;
    setIsLoading(true);
    setSearchError(null);

    try {
      const res = await userClient.searchUser({
        searchString: q,
        page: pageNum,
        pageSize: PAGE_SIZE,
      });
      if (mySeq !== searchSeqRef.current) {
        return;
      }
      setUsers((prev) => (append ? [...prev, ...res.adminUsers] : res.adminUsers));
      setHasMore(res.hasMore);
    } catch (error) {
      if (mySeq !== searchSeqRef.current) {
        return;
      }
      console.error(error);
      if (!append) {
        setUsers([]);
        setSearchError('Search failed. Please try again.');
      }
      setHasMore(false);
    } finally {
      if (mySeq === searchSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // On back-navigation: kick off the saved query and stash the scroll offset for later.
  // Scroll is NOT applied here — the container has no height until results render.
  useEffect(() => {
    if (savedState) {
      if (savedState.scrollY) {
        pendingScrollYRef.current = savedState.scrollY;
      }
      if (savedState.query && savedState.query.length >= 3) {
        handleSearch(savedState.query, 0, false);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Once results are rendered and the search is done, apply any deferred scroll offset.
  useEffect(() => {
    if (pendingScrollYRef.current != null && !isLoading && users.length > 0) {
      document.querySelector<HTMLElement>('.Content')?.scrollTo(0, pendingScrollYRef.current);
      pendingScrollYRef.current = null;
    }
  }, [users.length, isLoading]);

  const handleSearchDebounced = useDebouncedCallback((q: string) => {
    if (q.length < 3) {
      setUsers([]);
      setHasMore(false);
      return;
    }
    setPage(0);
    handleSearch(q, 0, false);
  }, 500);

  const handleQueryChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setUsers([]);
    setHasMore(false);
    setSearchError(null);
    handleSearchDebounced(nextQuery);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    handleSearch(query, nextPage, true);
  };

  const handleSelectUser = (id: string, user: AdminUser) => {
    // Stamp the current search state onto the /users history entry before navigating away.
    // Only the query and scroll offset are stored (not the full user array) to keep
    // history.state lean. On back-navigation the saved query triggers a re-fetch.
    navigate('.', {
      replace: true,
      state: {
        searchState: {
          query,
          scrollY: document.querySelector<HTMLElement>('.Content')?.scrollTop ?? 0,
        },
      },
    });
    navigate(`/users/${id}`, { state: { user } });
  };

  return (
    <Box padding={[0, 500, 500]}>
      <PageHeader title="Global Dining Users" />
      <div>
        <Box padding={[300, 0]}>
          <Box style={{ maxWidth: '50%' }}>
            <UserSearch query={query} onQueryChanged={handleQueryChanged} />
          </Box>
        </Box>
        <Divider />
        <Box padding={[300, 0]}>
          {searchError ? (
            <Text variant="body" color="fgDangerBase">
              {searchError}
            </Text>
          ) : (
            <UserCards results={users} isLoading={isLoading} onSelectUser={handleSelectUser} />
          )}
        </Box>
        {hasMore && !isLoading && (
          <Box padding={[300, 0]}>
            <Button label="Load more" onClick={handleLoadMore} />
          </Box>
        )}
        {/* Do we still need this in JCE world? */}
        {/* <SelfOnboardingVerification /> */}
      </div>
    </Box>
  );
};

export default UsersRoute;
